'use client'

import React, { useCallback, useEffect, useId, useState } from 'react'
import { WagamourV2Modal } from '@/components/v2/WagamourV2Modal'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import {
  WagamourV2ModalEyebrow,
  WagamourV2ModalStoryLine,
} from '@/components/v2/WagamourV2ModalContent'
import { PlayCircleOrbitAvatar } from '@/components/play/PlayCircleOrbitAvatar'
import { PlayStatusGlyph } from '@/components/play/PlayStatusGlyph'
import type { PlayRelationshipSheetContent } from '@/lib/play/playRelationshipContent'
import type { PlayCircleOrbitFriend } from '@/lib/play/playCircleOrbitMapper'
import { v2ModalErrorBannerStyle, v2ModalStyles } from '@/lib/design/v2ModalContent'

const PAIR_AVATAR_SIZE = 56

export type PlayRelationshipSheetProps = {
  isOpen: boolean
  activeDog: { name: string; photoUrl?: string | null }
  friend: PlayCircleOrbitFriend | null
  content: PlayRelationshipSheetContent | null
  onClose: () => void
  onPlanPlaydate: () => void
  onReviewPlaydate?: () => void
  /** Confirmed complete — parent runs network; returns ok/message. */
  onCompletePlaydate?: () => Promise<{ ok: boolean; message?: string }> | void
  /** Confirmed cancel — parent runs network; returns ok/message. */
  onCancelPlaydate?: () => Promise<{ ok: boolean; message?: string }> | void
  /** Optional banner after a successful complete/cancel while sheet stays open. */
  outcomeBanner?: string | null
  portalContainer?: HTMLElement | null
}

type Phase = 'main' | 'confirm-complete' | 'confirm-cancel'

function RelationshipPairVisual({
  activeDog,
  friend,
}: {
  activeDog: { name: string; photoUrl?: string | null }
  friend: PlayCircleOrbitFriend
}) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center justify-center gap-3" aria-hidden>
        <PlayCircleOrbitAvatar
          name={activeDog.name}
          photoUrl={activeDog.photoUrl}
          size={PAIR_AVATAR_SIZE}
          variant="center"
        />
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border"
          style={{
            borderColor: 'rgba(251, 191, 36, 0.32)',
            background: 'rgba(251, 191, 36, 0.1)',
          }}
        >
          <PlayStatusGlyph size={18} className="text-amber-100/90" active />
        </div>
        <PlayCircleOrbitAvatar
          name={friend.name}
          photoUrl={friend.photoUrl}
          size={PAIR_AVATAR_SIZE}
          variant="friend"
        />
      </div>
    </div>
  )
}

function RelationshipSheetBody({
  content,
  outcomeBanner,
}: {
  content: PlayRelationshipSheetContent
  outcomeBanner?: string | null
}) {
  const hasStory =
    Boolean(content.upcomingPlaydate) ||
    Boolean(content.firstMet) ||
    Boolean(content.lastPlayed) ||
    Boolean(content.timesPlayedTogether) ||
    Boolean(content.relationshipAge)

  return (
    <div className="space-y-5">
      <p className="text-center text-[15px] leading-relaxed" style={v2ModalStyles.body}>
        {content.contextLine}
      </p>
      {outcomeBanner ? (
        <p
          className="rounded-[16px] border border-amber-200/30 bg-amber-50/10 px-3 py-2 text-center text-sm text-amber-50"
          data-play-relationship-outcome="true"
          role="status"
        >
          {outcomeBanner}
        </p>
      ) : null}
      {content.statusLine ? (
        <p
          className="text-center text-sm font-medium text-amber-100/90"
          data-play-relationship-status="true"
        >
          {content.statusLine}
        </p>
      ) : null}
      <div className="space-y-3.5 rounded-[20px] border px-4 py-4" style={v2ModalStyles.nestedCard}>
        {content.upcomingPlaydate ? (
          <WagamourV2ModalStoryLine label="Next meetup">
            {content.upcomingPlaydate}
          </WagamourV2ModalStoryLine>
        ) : null}
        {content.lastPlayed ? (
          <WagamourV2ModalStoryLine label="Last played">{content.lastPlayed}</WagamourV2ModalStoryLine>
        ) : null}
        {content.timesPlayedTogether ? (
          <WagamourV2ModalStoryLine label="Times together">
            {content.timesPlayedTogether}
          </WagamourV2ModalStoryLine>
        ) : null}
        {content.relationshipAge ? (
          <WagamourV2ModalStoryLine label="Friends for">
            {content.relationshipAge}
          </WagamourV2ModalStoryLine>
        ) : null}
        {content.firstMet ? (
          <WagamourV2ModalStoryLine label="How you met">{content.firstMet}</WagamourV2ModalStoryLine>
        ) : null}
        {!hasStory ? (
          <WagamourV2ModalStoryLine label="Circle">
            Trusted connection from a real meeting. Plan a playdate when you're ready.
          </WagamourV2ModalStoryLine>
        ) : null}
      </div>
    </div>
  )
}

/** Production relationship sheet — API-backed copy, no preview fixtures. */
export function PlayRelationshipSheet({
  isOpen,
  activeDog,
  friend,
  content,
  onClose,
  onPlanPlaydate,
  onReviewPlaydate,
  onCompletePlaydate,
  onCancelPlaydate,
  outcomeBanner = null,
  portalContainer = null,
}: PlayRelationshipSheetProps) {
  const titleId = useId()
  const [phase, setPhase] = useState<Phase>('main')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setPhase('main')
      setBusy(false)
      setActionError(null)
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    if (busy) return
    onClose()
  }, [busy, onClose])

  const handlePrimary = useCallback(() => {
    const action = content?.primaryAction ?? 'plan'
    if (action === 'review') {
      onReviewPlaydate?.()
      return
    }
    if (action === 'complete') {
      setActionError(null)
      setPhase('confirm-complete')
      return
    }
    if (action === 'close') {
      onClose()
      return
    }
    onPlanPlaydate()
  }, [content?.primaryAction, onClose, onPlanPlaydate, onReviewPlaydate])

  const runComplete = useCallback(async () => {
    if (!onCompletePlaydate || busy) return
    setBusy(true)
    setActionError(null)
    try {
      const result = await onCompletePlaydate()
      if (result && !result.ok) {
        setActionError(result.message ?? 'Could not complete playdate. Try again.')
        setPhase('main')
        return
      }
      setPhase('main')
    } catch {
      setActionError('Network error — try again.')
      setPhase('main')
    } finally {
      setBusy(false)
    }
  }, [busy, onCompletePlaydate])

  const runCancel = useCallback(async () => {
    if (!onCancelPlaydate || busy) return
    setBusy(true)
    setActionError(null)
    try {
      const result = await onCancelPlaydate()
      if (result && !result.ok) {
        setActionError(result.message ?? 'Could not cancel playdate. Try again.')
        setPhase('main')
        return
      }
      setPhase('main')
    } catch {
      setActionError('Network error — try again.')
      setPhase('main')
    } finally {
      setBusy(false)
    }
  }, [busy, onCancelPlaydate])

  if (!friend || !content) return null

  const showCancel = Boolean(content.canCancelPlaydate && onCancelPlaydate)
  const isCloseOnly = content.primaryAction === 'close'

  return (
    <WagamourV2Modal
      isOpen={isOpen}
      onClose={handleClose}
      presentation="sheet"
      portalContainer={portalContainer}
      preventClose={busy}
      ariaLabelledBy={titleId}
      header={
        <div className="space-y-1">
          <WagamourV2ModalEyebrow>Play relationship</WagamourV2ModalEyebrow>
          <h2 id={titleId} className="text-base font-bold leading-snug" style={v2ModalStyles.headline}>
            {phase === 'confirm-complete'
              ? 'Confirm you met?'
              : phase === 'confirm-cancel'
                ? 'Cancel playdate?'
                : content.pairTitle}
          </h2>
        </div>
      }
      footer={
        phase === 'confirm-complete' ? (
          <div className="space-y-2">
            <WagamourV2Button
              type="button"
              fullWidth
              disabled={busy}
              onClick={() => void runComplete()}
              data-play-relationship-confirm-complete="true"
            >
              {busy ? 'Saving…' : 'Yes, we met'}
            </WagamourV2Button>
            <WagamourV2Button
              type="button"
              fullWidth
              variant="secondary"
              disabled={busy}
              onClick={() => setPhase('main')}
            >
              Keep playdate
            </WagamourV2Button>
          </div>
        ) : phase === 'confirm-cancel' ? (
          <div className="space-y-2">
            <WagamourV2Button
              type="button"
              fullWidth
              variant="destructive"
              disabled={busy}
              onClick={() => void runCancel()}
              data-play-relationship-confirm-cancel="true"
            >
              {busy ? 'Cancelling…' : 'Yes, cancel playdate'}
            </WagamourV2Button>
            <WagamourV2Button
              type="button"
              fullWidth
              disabled={busy}
              onClick={() => setPhase('main')}
            >
              Keep playdate
            </WagamourV2Button>
          </div>
        ) : (
          <div className="space-y-2">
            <WagamourV2Button
              type="button"
              fullWidth
              disabled={busy}
              onClick={handlePrimary}
              data-play-relationship-primary-action={content.primaryAction ?? 'plan'}
            >
              {content.primaryCta}
            </WagamourV2Button>
            {showCancel && !isCloseOnly ? (
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setActionError(null)
                  setPhase('confirm-cancel')
                }}
                data-play-relationship-cancel-playdate="true"
              >
                Cancel playdate
              </WagamourV2Button>
            ) : null}
            {!isCloseOnly ? (
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={busy}
                onClick={handleClose}
              >
                {content.secondaryCta}
              </WagamourV2Button>
            ) : null}
            {actionError ? (
              <p
                className="rounded-[16px] border px-3 py-2 text-center text-xs"
                role="alert"
                style={v2ModalErrorBannerStyle()}
              >
                {actionError}
              </p>
            ) : null}
          </div>
        )
      }
    >
      <div className="space-y-5" aria-labelledby={titleId}>
        {phase === 'confirm-complete' ? (
          <p className="text-center text-sm leading-relaxed" style={v2ModalStyles.body}>
            This updates your friendship history. {friend.name} stays in your Play Circle.
          </p>
        ) : phase === 'confirm-cancel' ? (
          <p className="text-center text-sm leading-relaxed" style={v2ModalStyles.body}>
            The playdate ends, but you and {friend.name} stay friends.
          </p>
        ) : (
          <>
            <RelationshipPairVisual activeDog={activeDog} friend={friend} />
            <RelationshipSheetBody content={content} outcomeBanner={outcomeBanner} />
            {actionError ? (
              <p
                className="rounded-[16px] border px-3 py-2 text-center text-xs"
                role="alert"
                style={v2ModalErrorBannerStyle()}
              >
                {actionError}
              </p>
            ) : null}
          </>
        )}
      </div>
    </WagamourV2Modal>
  )
}
